import React, { FC, useEffect, useState } from 'react';
import qs from 'qs';
import { useHistory, useLocation } from 'react-router-dom';
import api from '@src/util/api';
import { Button } from '@src/components/Button';
import { setRecruiter, fetchCustomerStatus } from '@src/redux/actions/recruiter';
import { fetchRefreshToken } from '@src/redux/actions/accessToken';
import { setCompany } from '@src/redux/actions/company';
import { setModalContent } from '@src/redux/actions/modal';
import { setFlashMessage } from '@src/redux/actions/flashMessages';
import { getModalIsOpen } from '@src/redux/selectors/modal';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import { getActiveMetricsContext, trackEvent, trackSuperProperties } from '@src/metrics';
import { openActivateSubscriptionModal } from '@src/redux/actions/subscription';
import { LOGIN_ROUTE, QUERY_PARAMS } from '@src/Routes/links';
import { useSessionLazyQuery, useSwitchedWorkspaceSubscription } from '@src/graphql/generated';
import { bootIntercom } from '@src/intercom';
import { useAppDispatch, useAppSelector } from '@src/redux';
import { SwitchCompanyModal } from '../SwitchCompanyModal';
import { FinishProfileModal } from '../FinishProfileModal';
import { SessionProps } from './props';

export const Session: FC<SessionProps> = ({ children, isProfileFinishModalDisabled }) => {
  const dispatch = useAppDispatch();

  const isModalOpen = useAppSelector(getModalIsOpen);

  const history = useHistory();

  const location = useLocation();

  const [isError, setIsError] = useState(false);

  const [fetchSessionQuery, { data }] = useSessionLazyQuery({
    fetchPolicy: 'network-only',
  });

  const company = data?.company;
  const recruiter = data?.currentRecruiter;

  useSwitchedWorkspaceSubscription({
    onSubscriptionData: ({ subscriptionData }) => {
      const toCompany = subscriptionData.data?.switchedWorkspace.toCompany;

      if (!toCompany?.id || toCompany?.id === company?.id || isModalOpen) {
        return;
      }

      dispatch(
        setModalContent({
          className: 'opening-modal-wrapper',
          config: {
            disableClose: true,
            hideCloseButton: true,
          },
          content: <SwitchCompanyModal activeCompanyId={toCompany?.id} activeCompanyName={toCompany.profile.name} />,
          title: 'Workspace changed',
        }),
      );
    },
    skip: !company?.id || !recruiter?.id || recruiter?.availableCompanies.length <= 1,
  });

  const checkQueryParams = () => {
    const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });

    if (queryParams[QUERY_PARAMS.ACTIVATE_SUBSCRIPTION.key] === QUERY_PARAMS.ACTIVATE_SUBSCRIPTION.value) {
      delete queryParams[QUERY_PARAMS.ACTIVATE_SUBSCRIPTION.key];

      history.replace({
        pathname: location.pathname,
        search: qs.stringify(queryParams),
      });

      dispatch(openActivateSubscriptionModal());
    }
  };

  const createSession = async (queryParams?: Record<string, string>) => {
    const { loginToken, ...queryParamsWithOutToken } = queryParams || {};

    try {
      await api.post('/api/session', { loginToken });

      history.replace({
        pathname: location.pathname,
        search: qs.stringify(queryParamsWithOutToken),
      });
    } catch (error) {
      setIsError(true);

      window.console.error(error);
    }
  };

  useEffect(() => {
    const fetchSessionData = async () => {
      const queryParams: Record<string, string> | undefined = qs.parse(location.search, {
        ignoreQueryPrefix: true,
      }) as Record<string, string>;

      const { loginToken, ...queryParamsWithOutToken } = queryParams;

      if (loginToken) {
        try {
          await api.head('/api/token/refresh');

          history.replace({
            pathname: location.pathname,
            search: qs.stringify(queryParamsWithOutToken),
          });
        } catch (error: any) {
          if (error.code === 401) {
            await createSession(queryParams);
          }
        }
      }

      try {
        await dispatch(fetchRefreshToken());

        await dispatch(fetchCustomerStatus());

        const { data } = await fetchSessionQuery();

        const company = data?.company;
        const recruiter = data?.currentRecruiter;
        const customer = data?.customer;

        if (recruiter) {
          dispatch(setRecruiter(recruiter));
        }

        dispatch(setCompany(company));

        if (recruiter && recruiter?.availableCompanies.length > 1) {
          dispatch(
            setFlashMessage({
              linkLabel: 'Got it',
              message: `You are now seeing ${company?.profile.name} job openings & applicants.`,
              title: `Signed in to ${company?.profile.name}`,
              type: 'success',
            }),
          );
        }

        if ((recruiter?.isDraft || recruiter?.isIncomplete) && !isProfileFinishModalDisabled) {
          dispatch(
            setModalContent({
              className: 'finish-recruiter-modal-wrapper',
              config: { disableClose: true },
              content: <FinishProfileModal />,
              doNotCloseOnLocationChange: true,
            }),
          );

          trackEvent({
            context: MetricContext.FINISH_RECRUITER_PROFILE,
            interaction: MetricAction.OPEN_FINISH_RECRUITER_PROFILE_VIEW,
            payload: {
              'Event Source': MetricEventSource.USER_LOGIN,
              'Previous Context': getActiveMetricsContext(location.pathname),
            },
          });
        }

        if (recruiter) {
          await bootIntercom();
        }

        checkQueryParams();

        trackSuperProperties({
          planType: customer?.planType,
          subscriptionPrice: customer?.planInfo?.price,
          subscriptionState: customer?.planInfo?.state,
        });
      } catch (error: any) {
        if (error.code === 401) {
          history.replace({ pathname: LOGIN_ROUTE });
        } else {
          window.console.error(error);
        }
      }
    };

    fetchSessionData();
  }, []);

  if (isError) {
    return (
      <div className="session-error">
        <h2>Looks like this link has expired.</h2>

        <h5>Please contact support or your account manager.</h5>

        <br />

        <Button isLink to={LOGIN_ROUTE}>
          Company login
        </Button>
      </div>
    );
  }

  if (!recruiter) {
    return null;
  }

  return <>{children}</>;
};
